import { AxiosError } from 'axios';
import { apiClient } from './api';
import type { ServiceZone } from '@/types';

export interface City {
  id: string;
  name: string;
  slug: string;
}

interface CheckZoneResponse {
  inServiceZone: boolean;
  zone?: ServiceZone;
  message?: string;
}

interface CheckRestaurantZoneResponse {
  inServiceZone: boolean;
  zone?: {
    id: string;
    name: string;
    deliveryFee?: number;
    minimumOrderAmount?: number;
    estimatedDeliveryMinutes?: number;
  };
  message?: string;
}

function restaurantZoneCheckPaths(restaurantId: string): string[] {
  return [
    `/catalog/public/restaurants/${restaurantId}/zones/check-address`,
    `/geo/restaurants/${restaurantId}/check-delivery-zone`,
    `/catalog/restaurants/${restaurantId}/zones/check-address`,
  ];
}

function parseRestaurantZoneResponse(
  response: { data: { data: CheckRestaurantZoneResponse } },
): { id: string; name: string } | null {
  const result = response.data.data;

  if (!result.inServiceZone || !result.zone) {
    return null;
  }

  return { id: result.zone.id, name: result.zone.name };
}

export const geoService = {
  async checkServiceZone(latitude: number, longitude: number): Promise<ServiceZone | null> {
    const response = await apiClient.post<CheckZoneResponse>('/geo/check-zone', {
      latitude,
      longitude,
    });

    const result = response.data.data;

    if (!result.inServiceZone || !result.zone) {
      return null;
    }

    return result.zone;
  },

  async checkRestaurantDeliveryZone(
    restaurantId: string,
    latitude: number,
    longitude: number,
  ): Promise<{ id: string; name: string } | null> {
    const body = { latitude, longitude };
    let lastError: unknown;

    for (const path of restaurantZoneCheckPaths(restaurantId)) {
      try {
        const response = await apiClient.post<CheckRestaurantZoneResponse>(path, body);
        return parseRestaurantZoneResponse(response);
      } catch (error) {
        lastError = error;
        const status = (error as AxiosError)?.response?.status;
        if (status === 404) {
          continue;
        }
        throw error;
      }
    }

    throw lastError ?? new Error('Restaurant delivery zone check is unavailable');
  },

  async getCities(): Promise<{ cities: City[]; total: number }> {
    const response = await apiClient.get<{ cities: City[]; total: number }>('/geo/cities');
    return response.data.data;
  },

  async getServiceZones(): Promise<{ zones: ServiceZone[]; total: number }> {
    const response = await apiClient.get<{ zones: ServiceZone[]; total: number }>('/geo/zones');
    return response.data.data;
  },

  async getZonesByCity(city: string): Promise<ServiceZone[]> {
    if (!city?.trim()) return [];
    const response = await apiClient.get<{ city: string; zones: ServiceZone[]; total: number }>(
      `/geo/zones/city/${encodeURIComponent(city.trim())}`,
    );
    return response.data.data?.zones ?? [];
  },
};
